import React, { useState, useEffect } from "react";
import { useTranslation } from "../../../../i18n/LanguageContext";
import "./css/SelectedPlanPreview.css";
import SubscriptionsAPI from "../../services/SubscriptionsAPI";
import { formatDateOnly } from "../../utils/DateTimeUtil";
import { calculateNextPaymentDate } from "../../utils/SubscriptionUtil";
import { useSubscription } from "../../context/SubscriptionContext";

const SelectedPlanPreview = ({
  selectedPlan,
  currentSubscription,
  subscribing,
  handleSubscribeToPlan,
  handleClearSelectedPlan,
  handleViewPlans,
}) => {
  const { getPlanName, getMaxUsers, subscription, servicePlan, priceInfo } =
    useSubscription();
  const { t } = useTranslation();
  const isNewSubscription = !currentSubscription;

  // Use the base (pre-discount) price for upgrade/downgrade comparisons
  // so we compare apples-to-apples with selectedPlan.price_monthly (also base).
  const currentBasePrice =
    priceInfo?.basePrice || servicePlan?.priceMonthly || 0;

  const [startWithTrial, setStartWithTrial] = useState(false);
  const [autoRenew, setAutoRenew] = useState(true);

  // Promo code state
  const [promoCode, setPromoCode] = useState("");
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoResult, setPromoResult] = useState(null); // null = not validated, object = result

  // Trial eligibility state
  const [trialEligibility, setTrialEligibility] = useState({
    canStartTrial: true,
    isCurrentlyInTrial: false,
    existingTrialEndDate: null,
    reason: null,
    loading: true,
  });

  // Check trial eligibility on mount
  useEffect(() => {
    const checkEligibility = async () => {
      try {
        const response = await SubscriptionsAPI.checkTrialEligibility();
        if (response?.success) {
          setTrialEligibility({
            canStartTrial: response.canStartTrial,
            isCurrentlyInTrial: response.isCurrentlyInTrial,
            existingTrialEndDate: response.existingTrialEndDate,
            reason: response.reason,
            loading: false,
          });
          console.log("📋 Trial eligibility:", response);
        }
      } catch (error) {
        console.error("Error checking trial eligibility:", error);
        // On error, default to allowing trial for new subscriptions only
        setTrialEligibility({
          canStartTrial: isNewSubscription,
          isCurrentlyInTrial: false,
          existingTrialEndDate: null,
          reason: null,
          loading: false,
        });
      }
    };

    checkEligibility();
  }, [isNewSubscription]);

  // Determine if trial toggle should be shown
  const shouldShowTrialOption = () => {
    // Loading - don't show yet
    if (trialEligibility.loading) return false;

    // New subscription and eligible - show
    if (isNewSubscription && trialEligibility.canStartTrial) return true;

    // Currently in trial and changing plans - show (to preserve trial)
    if (trialEligibility.isCurrentlyInTrial) return true;

    // On paid subscription - don't show trial option
    if (!isNewSubscription && !currentSubscription?.isTrial) return false;

    // Already used trial - don't show
    if (trialEligibility.reason === "TRIAL_ALREADY_USED") return false;

    return trialEligibility.canStartTrial;
  };

  // Get reason text for why trial is not available
  const getTrialUnavailableReason = () => {
    if (trialEligibility.reason === "PAID_SUBSCRIPTION_ACTIVE") {
      return "Trial not available - you have an active paid subscription";
    }
    if (trialEligibility.reason === "TRIAL_ALREADY_USED") {
      return "Trial not available - you've already used your free trial";
    }
    return null;
  };

  // Calculate trial end date
  const getTrialEndDate = () => {
    // If changing plan during an existing trial, use the existing trial end date
    if (trialEligibility.existingTrialEndDate) {
      return new Date(trialEligibility.existingTrialEndDate);
    }
    if (
      !isNewSubscription &&
      currentSubscription?.isTrial &&
      currentSubscription?.trialEndDate
    ) {
      return new Date(currentSubscription.trialEndDate);
    }
    // Otherwise, calculate new trial end date (30 days from now)
    const trialEnd = new Date();
    trialEnd.setDate(trialEnd.getDate() + 30);
    return trialEnd;
  };

  // Reset promo code when plan changes
  useEffect(() => {
    setPromoCode("");
    setPromoResult(null);
  }, [selectedPlan?.plan_id]);

  // Validate promo code
  const handleValidatePromoCode = async () => {
    if (!promoCode.trim()) return;
    setPromoValidating(true);
    try {
      const response = await SubscriptionsAPI.validatePromoCode(
        promoCode.trim(),
        selectedPlan.plan_id,
        "MONTHLY",
      );
      if (response?.success) {
        setPromoResult(response);
      } else {
        setPromoResult({
          valid: false,
          message: response?.message || "Unable to validate code.",
        });
      }
    } catch (error) {
      console.error("Error validating promo code:", error);
      setPromoResult({
        valid: false,
        message: "Unable to validate code. Please try again.",
      });
    } finally {
      setPromoValidating(false);
    }
  };

  // Clear promo code
  const handleClearPromoCode = () => {
    setPromoCode("");
    setPromoResult(null);
  };

  // Get the effective price (after discount if applied)
  const getEffectivePrice = () => {
    if (promoResult?.valid && promoResult.discountedPrice != null) {
      return parseFloat(promoResult.discountedPrice);
    }
    return selectedPlan.price_monthly;
  };

  // Handle subscribe with promo code included
  const handleSubscribeWithPromo = (currentSub, trial, renew) => {
    const appliedPromoCode = promoResult?.valid
      ? promoCode.trim().toUpperCase()
      : null;
    const discountedPrice =
      promoResult?.valid && promoResult.discountedPrice != null
        ? parseFloat(promoResult.discountedPrice)
        : null;
    handleSubscribeToPlan(
      currentSub,
      trial,
      renew,
      appliedPromoCode,
      discountedPrice,
    );
  };

  return (
    <div className="common-form-group selected-plan-preview">
      <h4 className="selected-plan-preview__title">
        {isNewSubscription ? "🎯 Selected Plan" : "🔄 Plan Change Preview"}
      </h4>

      <div
        className={`common-alert ${!isNewSubscription && selectedPlan.price_monthly < currentBasePrice ? "warning" : "info"} selected-plan-preview__alert`}
      >
        {isNewSubscription ? (
          <p>
            <strong>{t("selectedPlan.alert.youHaveSelected")}</strong>
          </p>
        ) : (
          <div>
            <p>
              <strong>
                {selectedPlan.price_monthly < currentBasePrice
                  ? "⬇️ Plan Downgrade:"
                  : selectedPlan.price_monthly > currentBasePrice
                    ? "⬆️ Plan Upgrade:"
                    : "🔄 Plan Change:"}
              </strong>{" "}
              <span className="selected-plan-preview__alert-current">
                {getPlanName()}
              </span>
              {" → "}
              <span className="selected-plan-preview__alert-new">
                {selectedPlan.display_name}
              </span>
            </p>
            {selectedPlan.price_monthly < currentBasePrice && (
              <p
                className="selected-plan-preview__alert-text"
                style={{
                  fontSize: "0.9em",
                  marginTop: "6px",
                  color: "#92400e",
                }}
              >
                {t("selectedPlan.alert.downgradeNote")}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Plan Comparison Table */}
      {!isNewSubscription ? (
        <table className="selected-plan-preview__comparison-table">
          <thead>
            <tr>
              <th></th>
              <th>{t("selectedPlan.table.current")}</th>
              <th>{t("selectedPlan.table.new")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="selected-plan-preview__comparison-label">
                {t("selectedPlan.table.planName")}
              </td>
              <td>{getPlanName()}</td>
              <td className="selected-plan-preview__comparison-new">
                {selectedPlan.display_name}
              </td>
            </tr>
            <tr>
              <td className="selected-plan-preview__comparison-label">
                {t("selectedPlan.table.price")}
              </td>
              <td>
                {currentSubscription.amount === 0
                  ? t("selectedPlan.free")
                  : `$${currentSubscription.amount}/${currentSubscription.paymentCycle?.toUpperCase() === "YEARLY" ? "yr" : "mo"}`}
              </td>
              <td className="selected-plan-preview__comparison-new">
                {selectedPlan.price_monthly === 0 ? (
                  t("selectedPlan.free")
                ) : promoResult?.valid ? (
                  <>
                    <span
                      style={{ textDecoration: "line-through", opacity: 0.5 }}
                    >
                      ${selectedPlan.price_monthly}/mo
                    </span>{" "}
                    <strong style={{ color: "#16a34a" }}>
                      ${getEffectivePrice().toFixed(2)}/mo
                    </strong>
                    {startWithTrial && (
                      <span style={{ fontSize: "0.85em", color: "#6b7280" }}>
                        {" "}
                        {t("selectedPlan.afterTrial")}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    ${selectedPlan.price_monthly}/mo
                    {startWithTrial && (
                      <span style={{ fontSize: "0.85em", color: "#6b7280" }}>
                        {" "}
                        {t("selectedPlan.afterTrial")}
                      </span>
                    )}
                  </>
                )}
              </td>
            </tr>
            <tr>
              <td className="selected-plan-preview__comparison-label">
                {t("selectedPlan.table.maxUsers")}
              </td>
              <td>
                {currentSubscription.maxUsers || servicePlan?.maxUsers || "—"}
              </td>
              <td className="selected-plan-preview__comparison-new">
                {selectedPlan.max_users}
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <div className="selected-plan-preview__new-plan-summary">
          <div className="selected-plan-preview__summary-row">
            <span className="selected-plan-preview__summary-label">
              {t("selectedPlan.table.planName")}
            </span>
            <span className="selected-plan-preview__summary-value">
              {selectedPlan.display_name}
            </span>
          </div>
          <div className="selected-plan-preview__summary-row">
            <span className="selected-plan-preview__summary-label">
              {t("selectedPlan.table.price")}
            </span>
            <span className="selected-plan-preview__summary-value">
              {selectedPlan.price_monthly === 0 ? (
                t("selectedPlan.free")
              ) : promoResult?.valid ? (
                <>
                  <span
                    style={{ textDecoration: "line-through", opacity: 0.5 }}
                  >
                    ${selectedPlan.price_monthly}/mo
                  </span>{" "}
                  <strong style={{ color: "#16a34a" }}>
                    ${getEffectivePrice().toFixed(2)}/mo
                  </strong>
                </>
              ) : (
                `$${selectedPlan.price_monthly}/mo`
              )}
            </span>
          </div>
          <div className="selected-plan-preview__summary-row">
            <span className="selected-plan-preview__summary-label">
              {t("selectedPlan.table.maxUsers")}
            </span>
            <span className="selected-plan-preview__summary-value">
              {selectedPlan.max_users}
            </span>
          </div>
        </div>
      )}

      {/* Promo Code Section */}
      {selectedPlan.price_monthly > 0 && (
        <div className="selected-plan-preview__promo-section">
          <div className="selected-plan-preview__promo-input-group">
            <label className="selected-plan-preview__promo-label">
              🏷️ {t("selectedPlan.promo.label")}
            </label>
              {promoResult?.valid ? (
                /* Successfully applied promo code display */
                <div className="selected-plan-preview__promo-applied">
                  <div className="selected-plan-preview__promo-applied-badge">
                    <span className="selected-plan-preview__promo-check">
                      ✅
                    </span>
                    <span className="selected-plan-preview__promo-code-text">
                      {promoCode.toUpperCase()}
                    </span>
                    <button
                      className="selected-plan-preview__promo-remove"
                      onClick={handleClearPromoCode}
                      type="button"
                      title={t("selectedPlan.promo.removeTitle")}
                    >
                      ✕
                    </button>
                  </div>
                  <div className="selected-plan-preview__promo-discount-summary">
                    <span className="selected-plan-preview__promo-message-success">
                      {promoResult.message}
                    </span>
                    <div className="selected-plan-preview__promo-pricing">
                      <span className="selected-plan-preview__promo-original-price">
                        ${parseFloat(promoResult.originalPrice).toFixed(2)}/mo
                      </span>
                      <span className="selected-plan-preview__promo-arrow">
                        →
                      </span>
                      <span className="selected-plan-preview__promo-new-price">
                        ${parseFloat(promoResult.discountedPrice).toFixed(2)}/mo
                      </span>
                      <span className="selected-plan-preview__promo-savings">
                        (Save $
                        {parseFloat(promoResult.savingsAmount).toFixed(2)}/mo)
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                /* Promo code input form */
                <div className="selected-plan-preview__promo-form">
                  <div className="selected-plan-preview__promo-input-row">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => {
                        setPromoCode(e.target.value.toUpperCase());
                        if (promoResult) setPromoResult(null);
                      }}
                      placeholder={t("selectedPlan.promo.placeholder")}
                      className="selected-plan-preview__promo-input"
                      disabled={promoValidating}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleValidatePromoCode();
                        }
                      }}
                    />
                    <button
                      className="common-button primary selected-plan-preview__promo-apply-btn"
                      onClick={handleValidatePromoCode}
                      disabled={promoValidating || !promoCode.trim()}
                      type="button"
                    >
                      {promoValidating
                        ? t("selectedPlan.promo.checking")
                        : t("selectedPlan.promo.apply")}
                    </button>
                    {promoCode && (
                      <button
                        className="selected-plan-preview__promo-cancel-btn"
                        onClick={handleClearPromoCode}
                        type="button"
                        title="Clear"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {promoResult && !promoResult.valid && (
                    <div className="selected-plan-preview__promo-error">
                      ❌ {promoResult.message}
                    </div>
                  )}
                </div>
              )}
          </div>
        </div>
      )}

      {selectedPlan.trial_period_days > 0 && shouldShowTrialOption() && (
        <div className="selected-plan-preview__trial-info">
          <span className="selected-plan-preview__trial-badge">🎁 Trial</span>
          <span>
            $0 for {selectedPlan.trial_period_days} days — full access, then $
            {selectedPlan.price_monthly}/mo
          </span>
        </div>
      )}

      {/* Subscription Type and Auto-Renew */}
      <div className="common-form-row selected-plan-preview__toggles">
        {/* Subscription Type Toggle Buttons - Only show if eligible for trial */}
        {shouldShowTrialOption() && (
          <div className="common-form-field selected-plan-preview__toggle-box">
            <div className="selected-plan-preview__subscription-type-group">
              <div className="selected-plan-preview__toggle-button-row">
                <div className="selected-plan-preview__toggle-text">
                  <span className="selected-plan-preview__toggle-button-label">
                    {t("selectedPlan.toggle.fullSubscription")}
                  </span>
                </div>
                <div
                  className={`common-toggle ${!startWithTrial ? "active" : ""}`}
                  onClick={() => setStartWithTrial(false)}
                ></div>
              </div>
              <div className="selected-plan-preview__toggle-button-row">
                <div className="selected-plan-preview__toggle-text">
                  <span className="selected-plan-preview__toggle-button-label">
                    {trialEligibility.isCurrentlyInTrial
                      ? t("selectedPlan.toggle.continueWithTrial")
                      : t("selectedPlan.toggle.startWithTrial")}
                  </span>
                  {!trialEligibility.isCurrentlyInTrial && (
                    <span className="selected-plan-preview__trial-availability">
                      {t("selectedPlan.trial.availabilityNote")}
                    </span>
                  )}
                </div>
                <div
                  className={`common-toggle ${startWithTrial ? "active" : ""}`}
                  onClick={() => setStartWithTrial(true)}
                ></div>
              </div>
            </div>
            {startWithTrial && (
              <div className="selected-plan-preview__toggle-message">
                Trial will end on {formatDateOnly(getTrialEndDate(), "medium")}
                {trialEligibility.isCurrentlyInTrial && (
                  <span style={{ fontWeight: "bold", color: "#2563eb" }}>
                    {" "}
                    {t("selectedPlan.trial.notExtended")}
                  </span>
                )}
                <div className="selected-plan-preview__toggle-message-sub">
                  {t("selectedPlan.trial.cancelRiskFree")}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Auto-Renew Toggle */}
        <div className="common-form-field selected-plan-preview__toggle-box">
          <div className="selected-plan-preview__toggle-label-wrapper">
            <label className="selected-plan-preview__toggle-label">
              {t("selectedPlan.toggle.autoRenewLabel")}
            </label>
            <div
              className={`common-toggle ${autoRenew ? "active" : ""}`}
              onClick={() => setAutoRenew(!autoRenew)}
            ></div>
          </div>
          {autoRenew && (
            <div className="selected-plan-preview__toggle-message">
              {t("selectedPlan.toggle.autoRenewMessage")}
            </div>
          )}
        </div>
      </div>

      <div className="common-button-group selected-plan-preview__button-group">
        <button
          className="common-button primary selected-plan-preview__button--large"
          onClick={() =>
            handleSubscribeWithPromo(
              currentSubscription,
              startWithTrial,
              autoRenew,
            )
          }
          disabled={subscribing}
        >
          {subscribing
            ? isNewSubscription
              ? t("selectedPlan.btn.activating")
              : t("selectedPlan.btn.changingPlan")
            : isNewSubscription
              ? t("selectedPlan.btn.activatePlan")
              : t("selectedPlan.btn.subscribePlan")}
        </button>
        <button
          className="common-button secondary selected-plan-preview__button--large"
          onClick={handleViewPlans}
        >
          {t("selectedPlan.btn.chooseDifferent")}
        </button>
        <button
          className="common-button secondary"
          onClick={handleClearSelectedPlan}
        >
          {isNewSubscription
            ? t("selectedPlan.btn.cancelSelection")
            : t("selectedPlan.btn.cancelChange")}
        </button>
      </div>

      {/* Downgrade / Upgrade / New Subscription Details */}
      {(() => {
        if (isNewSubscription) {
          return (
            <div className="selected-plan-preview__details-card">
              <div className="selected-plan-preview__details-card-header">
                {t("selectedPlan.details.subscriptionDetails")}
              </div>
              <div className="selected-plan-preview__details-card-body">
                <div className="selected-plan-preview__detail-item">
                  <span className="selected-plan-preview__detail-icon">✅</span>
                  <span>{t("selectedPlan.details.activatedImmediately")}</span>
                </div>
                <div className="selected-plan-preview__detail-item">
                  <span className="selected-plan-preview__detail-icon">💰</span>
                  <span>
                    {selectedPlan.price_monthly === 0 ? (
                      t("selectedPlan.free")
                    ) : promoResult?.valid ? (
                      <>
                        <span
                          style={{
                            textDecoration: "line-through",
                            opacity: 0.5,
                          }}
                        >
                          ${selectedPlan.price_monthly}/month
                        </span>{" "}
                        <strong style={{ color: "#16a34a" }}>
                          ${getEffectivePrice().toFixed(2)}/month
                        </strong>{" "}
                        ({promoCode.toUpperCase()} applied)
                      </>
                    ) : (
                      `$${selectedPlan.price_monthly}/month`
                    )}
                  </span>
                </div>
                {selectedPlan.trial_period_days > 0 && (
                  <div className="selected-plan-preview__detail-item">
                    <span className="selected-plan-preview__detail-icon">
                      🎁
                    </span>
                    <span>
                      {t("selectedPlan.details.includesTrial").replace(
                        "{days}",
                        selectedPlan.trial_period_days,
                      )}
                    </span>
                  </div>
                )}
                <div className="selected-plan-preview__detail-item">
                  <span className="selected-plan-preview__detail-icon">💳</span>
                  <span>{t("selectedPlan.details.cancelAnytime")}</span>
                </div>
              </div>
            </div>
          );
        }

        const isDowngrade = selectedPlan.price_monthly < currentBasePrice;
        const isUpgrade = selectedPlan.price_monthly > currentBasePrice;
        const currentEndDate = calculateNextPaymentDate(
          currentSubscription?.startDate,
          currentSubscription?.paymentCycle,
          currentSubscription,
        );
        const formattedEndDate = currentEndDate
          ? formatDateOnly(currentEndDate, "medium")
          : "the end of your current billing period";
        const currentDiscountPct =
          parseFloat(currentSubscription?.discountPercent) || 0;

        if (isDowngrade) {
          return (
            <div className="selected-plan-preview__details-card downgrade">
              <div className="selected-plan-preview__details-card-header">
                {t("selectedPlan.details.downgradeDetails")}
              </div>
              <div className="selected-plan-preview__details-card-body">
                {/* Timeline */}
                <div className="selected-plan-preview__timeline">
                  <div className="selected-plan-preview__timeline-step active">
                    <div className="selected-plan-preview__timeline-dot active"></div>
                    <div className="selected-plan-preview__timeline-content">
                      <strong>
                        {t("selectedPlan.downgrade.keepUntil").replace(
                          "{date}",
                          formattedEndDate,
                        )}
                      </strong>
                      <span>
                        {t("selectedPlan.downgrade.keepYourPlan")}{" "}
                        <strong>{getPlanName()}</strong>{" "}
                        {t("selectedPlan.downgrade.plan")}
                        {currentDiscountPct > 0
                          ? ` (${currentDiscountPct}% off)`
                          : ""}{" "}
                        {t("selectedPlan.downgrade.at")}{" "}
                        <strong>
                          {currentSubscription.amount === 0
                            ? t("selectedPlan.free")
                            : `$${currentSubscription.amount}/${currentSubscription.paymentCycle?.toUpperCase() === "YEARLY" ? "yr" : "mo"}`}
                        </strong>
                        {currentDiscountPct > 0
                          ? ` ${t("selectedPlan.details.includesDiscount")}`
                          : ""}
                      </span>
                      <span className="selected-plan-preview__timeline-sub">
                        {t("selectedPlan.downgrade.fullAccessNote").replace(
                          "{users}",
                          currentSubscription.maxUsers || "all",
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="selected-plan-preview__timeline-step upcoming">
                    <div className="selected-plan-preview__timeline-dot upcoming"></div>
                    <div className="selected-plan-preview__timeline-content">
                      <strong>
                        {t("selectedPlan.downgrade.afterDate").replace(
                          "{date}",
                          formattedEndDate,
                        )}
                      </strong>
                      <span>
                        {t("selectedPlan.downgrade.switchTo")}{" "}
                        <strong>{selectedPlan.display_name}</strong>{" "}
                        {t("selectedPlan.downgrade.at")}{" "}
                        <strong>
                          {selectedPlan.price_monthly === 0 ? (
                            t("selectedPlan.free")
                          ) : promoResult?.valid ? (
                            <>
                              <span
                                style={{
                                  textDecoration: "line-through",
                                  opacity: 0.5,
                                }}
                              >
                                ${selectedPlan.price_monthly}/mo
                              </span>{" "}
                              <span style={{ color: "#16a34a" }}>
                                ${getEffectivePrice().toFixed(2)}/mo
                              </span>
                            </>
                          ) : (
                            `$${selectedPlan.price_monthly}/mo`
                          )}
                        </strong>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Discount warning */}
                {currentDiscountPct > 0 && (
                  <div className="selected-plan-preview__warning-banner">
                    <span className="selected-plan-preview__warning-icon">
                      ⚠️
                    </span>
                    <span>
                      Your current {currentDiscountPct}%{" "}
                      {t("selectedPlan.details.discountMayNotApply").replace(
                        "{pct}",
                        "",
                      )}
                      {promoResult?.valid
                        ? " " + t("selectedPlan.details.promoApplied")
                        : " " + t("selectedPlan.details.usePromoHint")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        }

        // Upgrade or lateral change
        return (
          <div className="selected-plan-preview__details-card">
            <div className="selected-plan-preview__details-card-header">
              {isUpgrade
                ? t("selectedPlan.details.upgradeDetails")
                : t("selectedPlan.details.planChangeDetails")}
            </div>
            <div className="selected-plan-preview__details-card-body">
              <div className="selected-plan-preview__detail-item">
                <span className="selected-plan-preview__detail-icon">⚡</span>
                <span>
                  {isUpgrade
                    ? t("selectedPlan.details.upgradedImmediately")
                    : t("selectedPlan.details.updatedImmediately")}
                </span>
              </div>
              <div className="selected-plan-preview__detail-item">
                <span className="selected-plan-preview__detail-icon">💰</span>
                <span>
                  {isUpgrade
                    ? t("selectedPlan.details.proratedCharge")
                    : t("selectedPlan.details.noAdditionalCharges")}
                </span>
              </div>

              {/* Price change row */}
              <div className="selected-plan-preview__price-change-row">
                <div className="selected-plan-preview__price-block old">
                  <span className="selected-plan-preview__price-block-label">
                    {t("selectedPlan.table.currentPlan")}
                  </span>
                  <span className="selected-plan-preview__price-block-value">
                    {currentSubscription.amount === 0
                      ? t("selectedPlan.free")
                      : `$${currentSubscription.amount}/${currentSubscription.paymentCycle?.toUpperCase() === "YEARLY" ? "yr" : "mo"}`}
                    {currentDiscountPct > 0
                      ? ` (${currentDiscountPct}% off)`
                      : ""}
                  </span>
                </div>
                <span className="selected-plan-preview__price-arrow">→</span>
                <div className="selected-plan-preview__price-block new">
                  <span className="selected-plan-preview__price-block-label">
                    {t("selectedPlan.table.newPlan")}
                  </span>
                  <span className="selected-plan-preview__price-block-value">
                    {selectedPlan.price_monthly === 0 ? (
                      t("selectedPlan.free")
                    ) : promoResult?.valid ? (
                      <>
                        <span
                          style={{
                            textDecoration: "line-through",
                            opacity: 0.5,
                          }}
                        >
                          ${selectedPlan.price_monthly}/mo
                        </span>{" "}
                        <strong style={{ color: "#16a34a" }}>
                          ${getEffectivePrice().toFixed(2)}/mo
                        </strong>
                        {startWithTrial && (
                          <div
                            style={{
                              fontSize: "0.8em",
                              color: "#6b7280",
                              marginTop: "2px",
                            }}
                          >
                            {t("selectedPlan.afterTrial")}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        ${selectedPlan.price_monthly}/mo
                        {startWithTrial && (
                          <div
                            style={{
                              fontSize: "0.8em",
                              color: "#6b7280",
                              marginTop: "2px",
                            }}
                          >
                            {t("selectedPlan.afterTrial")}
                          </div>
                        )}
                      </>
                    )}
                  </span>
                </div>
              </div>

              {currentDiscountPct > 0 && isUpgrade && !promoResult?.valid && (
                <div className="selected-plan-preview__warning-banner">
                  <span className="selected-plan-preview__warning-icon">
                    ⚠️
                  </span>
                  <span>
                    Your current {currentDiscountPct}%{" "}
                    {t("selectedPlan.details.discountMayNotCarry").replace(
                      "{pct}",
                      "",
                    )}
                  </span>
                </div>
              )}
              {selectedPlan.trial_period_days > 0 &&
                shouldShowTrialOption() && (
                  <div className="selected-plan-preview__detail-item">
                    <span className="selected-plan-preview__detail-icon">
                      🎁
                    </span>
                    <span>
                      {t("selectedPlan.details.includesTrial").replace(
                        "{days}",
                        selectedPlan.trial_period_days,
                      )}
                    </span>
                  </div>
                )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default SelectedPlanPreview;
